import { createSlice } from "@reduxjs/toolkit";
import { dealsActions } from "./deals.action";

interface LostReason {
    id: string;
    label: string;
}

interface Deal {
    id: string;
    deal_name: string;
    pipeline: string;
    stage: string;
    amount: number;
    expected_close_date: string;
    tags: string[];
    owner: string;
    contacts: string[];
    deal_status: string;
    lost_reason: string | null;
}


interface InitialStateTypes {
    lostReasons: {
        data: LostReason[];
        loading: boolean;
        error: string | null;
        success: boolean;
    };
    deals: {
        data: Deal[];
        loading: boolean;
        error: string | null;
        success: boolean;
    };

    data: {
        allDeals: any[];
        length: any;
        allPipelines: any[];
        singleDeal: any;
        getDeal: any;
    };
    pagination: {
        current_page: Number,
        total_pages: Number,
        data: any;
        total: any;
        // limit: any;
        offset: any;
    },
    error: string;
    loading: boolean;
    success: boolean;
}

const initialState: InitialStateTypes = {
    lostReasons: {
        data: [],
        loading: false,
        error: null,
        success: false,
    },
    deals: {
        data: [],
        loading: false,
        error: null,
        success: false,
    },
    data: {
        allPipelines: [],
        length: undefined,
        allDeals: [],
        singleDeal: {},
        getDeal: {}
    },
    pagination: {
        current_page: 1,
        total_pages: 1,
        data: [],
        total: 0,
        offset:0
    },
    error: "",
    loading: false,
    success: false,
};


const DealsSlice = createSlice({
    name: "DealsSlice",
    initialState,
    reducers: {
        resetPipelineState: (state) => {
            state.error = "";
            state.success = false;
            state.loading = false;
        },
        resetLostReasonsState: (state) => {
            state.lostReasons.error = null;
            state.lostReasons.success = false;
            state.lostReasons.loading = false;
        },
        resetDealState: (state) => {
            state.deals.loading = false;
            state.deals.error = null;
            state.deals.success = false;
        },
        setDealData: (state, {payload})=>{
            state.data.getDeal = payload;
        }
    },
    extraReducers: (builder) => {
        // Create Pipeline
        builder
            .addCase(dealsActions.createPipeline.pending, (state) => {
                state.error = "";
                state.loading = true;
                state.success = false;
            })
            .addCase(dealsActions.createPipeline.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.error = "";
            })
            .addCase(dealsActions.createPipeline.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error = action?.error?.message || "An error occured";
            });
        // Get All Pipelines
        builder
            .addCase(dealsActions.getAllPipelines.pending, (state) => {
                state.error = "";
                state.loading = true;
            })
            .addCase(dealsActions.getAllPipelines.fulfilled, (state, action) => {
                // state.data.allPipelines = action.payload || [];
                state.data.allPipelines = Array.isArray(action.payload.data) ? action.payload.data : [];
                state.loading = false;
                state.error = ""; // Clear error if any
            })
            .addCase(dealsActions.getAllPipelines.rejected, (state, action) => {
                state.loading = false;
                state.error = action?.error?.message || "Failed to fetch pipelines";
            });
        //update deal by id..........
        builder
            .addCase(dealsActions.updateDealById.pending, (state) =>{
                state.error = "";
                state.loading = true;
                state.success = false;
            })
            .addCase(dealsActions.updateDealById.fulfilled,(state) => {
                state.error = "";
                state.loading = false;
                state.success = true;
            })
            .addCase(dealsActions.updateDealById.rejected, (state, action) =>{
                state.loading = false;
                state.success = false;
                state.error = action?.error?.message || "Failed to update deal";
            })
        //update pipeline
        builder
            .addCase(dealsActions.updatePipeline.pending, (state) => {
                state.error = "";
                state.loading = true;
                state.success = false;
            })
            .addCase(dealsActions.updatePipeline.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.error = "";

                const updatedPipelineIndex = state.data.allPipelines.findIndex(
                    (pipeline) => pipeline.id === action.payload.data.id
                );

                if (updatedPipelineIndex !== -1) {
                    state.data.allPipelines[updatedPipelineIndex] = action.payload.data;
                }
            })

            .addCase(dealsActions.updatePipeline.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error = action?.error?.message || "Failed to update pipeline";
            });


        //create lost reason
        builder
            .addCase(dealsActions.createLostReason.pending, (state) => {
                state.lostReasons.loading = true;
                state.lostReasons.error = null;
                state.lostReasons.success = false;
            })
            .addCase(dealsActions.createLostReason.fulfilled, (state, action) => {
                // Ensure the payload is structured properly
                const updatedLostReasons = action.payload?.data?.lost_reasons || [];
                state.lostReasons.data = Array.isArray(updatedLostReasons) ? updatedLostReasons : [];

                state.lostReasons.loading = false;
                state.lostReasons.success = true;
                state.lostReasons.error = null;
            })
            .addCase(dealsActions.createLostReason.rejected, (state, action) => {
                state.lostReasons.loading = false;
                state.lostReasons.success = false;
                // state.lostReasons.error =
                //     action.payload || "Failed to create or update lost reasons.";
            });

        //get lost reasons
        builder
            .addCase(dealsActions.getLostReasons.pending, (state) => {
                state.lostReasons.loading = true;
                state.lostReasons.error = null;
                state.lostReasons.success = false;
            })
            .addCase(dealsActions.getLostReasons.fulfilled, (state, action) => {

                // Extract lost_reasons from the payload, ensure data is structured correctly
                const lostReasonsData = action.payload?.data?.lost_reasons || [];
                state.lostReasons.data = Array.isArray(lostReasonsData) ? lostReasonsData : [];

                state.lostReasons.loading = false;
                state.lostReasons.success = true;
                state.lostReasons.error = null;
            })
            .addCase(dealsActions.getLostReasons.rejected, (state, action) => {
                state.lostReasons.loading = false;
                state.lostReasons.success = false;
                state.lostReasons.error = action?.error?.message || "Failed to fetch lost reasons";
            });


        // Delete Pipeline
        builder
            .addCase(dealsActions.deletePipeline.pending, (state) => {
                state.loading = true;
                state.error = "";
                state.success = false;
            })
            .addCase(dealsActions.deletePipeline.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.error = "";

                // Remove deleted pipeline from the state
                state.data.allPipelines = state.data.allPipelines.filter(
                    (pipeline) => pipeline.id !== action.meta.arg.pipelineId
                );
            })
            .addCase(dealsActions.deletePipeline.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error =
                    action?.error?.message || "Failed to delete pipeline";
            });

        // Delete Stage
        builder
            .addCase(dealsActions.deleteStage.pending, (state) => {
                state.loading = true;
                state.error = "";
                state.success = false;
            })
            .addCase(dealsActions.deleteStage.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.error = "";

                const { pipelineId, stageId } = action.meta.arg;

                // Find the target pipeline and remove the deleted stage
                const pipelineIndex = state.data.allPipelines.findIndex(
                    (pipeline) => pipeline.id === pipelineId
                );

                if (pipelineIndex !== -1) {
                    const updatedStages = state.data.allPipelines[pipelineIndex].stages.filter(
                        (stage: any) => stage.id !== stageId
                    );
                    state.data.allPipelines[pipelineIndex].stages = updatedStages;
                }
            })
            .addCase(dealsActions.deleteStage.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error =
                    action?.error?.message || "Failed to delete stage";
            });
        
        //get deal by id
        builder
            .addCase(dealsActions.getDealById.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(dealsActions.getDealById.fulfilled, (state,action)=>{
                const {data} = action.payload;
                state.data.singleDeal = data?.data || {};
                state.error = "";
                state.loading = false;
            })
            .addCase(dealsActions.getDealById.rejected, (state,action) => {
                state.loading = false;
                state.error = action?.error?.message || "Failed to fetch deal";
            })

        //get all deal
        builder
            .addCase(dealsActions.getDealList.pending, (state) => {
                state.loading = true;
                state.error = "";
            })
            .addCase(dealsActions.getDealList.fulfilled, (state, action) => {
                const { data, current_page, total_pages, total,offset } = action.payload;
                state.data.allDeals = Array.isArray(data) ? data : [];
                state.pagination = { current_page, total_pages,data, total,offset };
                state.loading = false;
                state.error = "";
            })
            .addCase(dealsActions.getDealList.rejected, (state, action) => {
                state.loading = false;
                state.error = action?.error?.message || "Failed to fetch Deals";
            });

        // Create Deal
        builder
            .addCase(dealsActions.createDeal.pending, (state) => {
                state.deals.loading = true;
                state.deals.error = null;
                state.deals.success = false;
            })
            .addCase(dealsActions.createDeal.fulfilled, (state, action) => {
                state.deals.loading = false;
                state.deals.success = true;
                state.deals.data.push(action.payload); // Append new deal
            })
            .addCase(dealsActions.createDeal.rejected, (state, action) => {
                state.deals.loading = false;
                state.deals.success = false;
                // state.deals.error = action.payload || "Failed to create the deal";
            });

        builder
            .addCase(dealsActions.deleteDeal.pending, (state) => {
                state.deals.loading = true;
                state.deals.error = null;
                state.deals.success = false;
            })
            .addCase(dealsActions.deleteDeal.fulfilled, (state, action) => {
                state.deals.loading = false;
                state.deals.success = true;

                // Remove the deleted deal from the state
                const { dealId } = action.payload;
                state.deals.data = state.deals.data.filter((deal) => deal.id !== action.payload.dealId);


                // state.data.allDeals = state.data.allDeals.filter((deal) => deal.id !== dealId);
            })
            .addCase(dealsActions.deleteDeal.rejected, (state, action) => {
                state.deals.loading = false;
                state.deals.success = false;
                // state.deals.error = action.payload || 'Failed to delete the deal';
            });

    }



});



export const { resetPipelineState,setDealData } = DealsSlice.actions;

export default DealsSlice.reducer;
